import { Stack, SxProps, Theme } from '@mui/material';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import { MouseEvent, RefObject, useCallback, useMemo, useRef, useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { EllipsesMenu, EllipsesMenuOption } from 'src/components/EllipsesMenu/ellipses-menu';
import { ShortcutText } from 'src/components/ShortcutText/shortcut-text';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { appShortcuts } from 'src/constants/appShortcuts';
import { useRegisterShortcuts } from 'src/utils/hooks/use-shortcuts';
import useResizeObserver from 'use-resize-observer';
import {
  OfferButton,
  OfferQuestion,
  StripButton,
  StripGroup,
  StripRecipient,
} from './composer-strip.styles';

const STRIP_ICON_SIZE = 15;

/** How many button labels still fit: all, all but More's, or none. */
type StripLabels = 'all' | 'noMore' | 'none';

type MeasuredStripLabels = Exclude<StripLabels, 'none'>;

function getStripLabels(
  availableWidth: number | undefined,
  buttonsWidths: Partial<Record<MeasuredStripLabels, number>>,
): StripLabels {
  if (availableWidth === undefined || buttonsWidths.all === undefined) return 'all';
  if (availableWidth >= buttonsWidths.all) return 'all';
  // an unmeasured step is tried first, so its width is known before the strip goes further
  if (buttonsWidths.noMore === undefined || availableWidth >= buttonsWidths.noMore) {
    return 'noMore';
  }
  return 'none';
}

// The offer row is a tighter pill than the strip: 12px left inset for the question, 6px right.
const offerGroupSx: SxProps<Theme> = { paddingLeft: '0.75rem', paddingRight: '0.375rem' };

export interface ComposerStartFoldedOffer {
  onAccept: () => void;
  onDecline: () => void;
}

interface ComposerStripProps {
  /**
   * The strip stays mounted while hidden; its key shortcuts only bind while it is shown, and
   * then it owns M and Z too, so a macro picked by key unfolds like one from More and a
   * snooze leaves it folded.
   */
  active: boolean;
  requesterFirstName?: string | null;
  showKeyHints: boolean;
  /**
   * When the column gets narrow, drops More's label first, then the other buttons' labels,
   * recipient and keys; a button that lost its label explains itself in a tooltip.
   */
  iconOnlyWhenNarrow?: boolean;
  onReply: () => void;
  onAddNote: () => void;
  onApplyMacro: () => void;
  onSnooze: () => void;
  offer?: ComposerStartFoldedOffer | null;
}

export const ComposerStrip = ({
  active,
  requesterFirstName,
  showKeyHints,
  iconOnlyWhenNarrow = false,
  onReply,
  onAddNote,
  onApplyMacro,
  onSnooze,
  offer,
}: ComposerStripProps) => {
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const { ref: stripRef, width: stripWidth } = useResizeObserver<HTMLDivElement>();

  // The group's width per label step, remembered from when that step was on screen: once a
  // step drops its labels the group shrinks and can no longer say how much room it needed.
  const [buttonsWidths, setButtonsWidths] = useState<
    Partial<Record<MeasuredStripLabels, number>>
  >({});
  const buttonsRef = useRef<HTMLDivElement>(null);
  const onButtonsResize = useCallback(({ width }: { width?: number }) => {
    // the step is read off the committed DOM, so a size reported for the previous step is
    // never filed under the one React has just rendered
    const measuredLabels = buttonsRef.current?.dataset.labels as StripLabels | undefined;
    if (width === undefined || !measuredLabels || measuredLabels === 'none') return;
    setButtonsWidths((widths) =>
      widths[measuredLabels] === width ? widths : { ...widths, [measuredLabels]: width },
    );
  }, []);
  useResizeObserver({
    // use-resize-observer still types `ref` as the pre-React-19 non-null RefObject; chat.tsx
    // bridges the same gap for its container ref
    ref: buttonsRef as RefObject<HTMLDivElement>,
    box: 'border-box',
    onResize: onButtonsResize,
  });

  const labels = iconOnlyWhenNarrow ? getStripLabels(stripWidth, buttonsWidths) : 'all';
  const isMoreIconOnly = labels !== 'all';
  const isIconOnly = labels === 'none';

  useRegisterShortcuts(
    useMemo(
      () =>
        active
          ? [
              {
                ...appShortcuts.openComposerReply,
                callback: () => {
                  onReply();
                  return false;
                },
              },
              {
                ...appShortcuts.openComposerNote,
                callback: () => {
                  onAddNote();
                  return false;
                },
              },
              {
                ...appShortcuts.openMacros,
                callback: () => {
                  onApplyMacro();
                  return false;
                },
              },
              {
                ...appShortcuts.openSnooze,
                callback: () => {
                  onSnooze();
                  return false;
                },
              },
            ]
          : [],
      [active, onReply, onAddNote, onApplyMacro, onSnooze],
    ),
  );

  const openMore = useCallback((event: MouseEvent<HTMLElement>) => {
    setMoreAnchor(event.currentTarget);
  }, []);
  const closeMore = useCallback(() => setMoreAnchor(null), []);

  const moreOptions = useMemo<EllipsesMenuOption[]>(
    () => [
      {
        label: 'Apply a macro',
        icon: 'zap',
        iconLibrary: 'lucide',
        shortcut: showKeyHints ? appShortcuts.openMacros.command : undefined,
        onClick: onApplyMacro,
      },
      {
        label: 'Snooze',
        icon: 'alarm-clock',
        iconLibrary: 'lucide',
        shortcut: showKeyHints ? appShortcuts.openSnooze.command : undefined,
        onClick: onSnooze,
      },
    ],
    [onApplyMacro, onSnooze, showKeyHints],
  );

  const replyLabel = requesterFirstName ? `Reply to ${requesterFirstName}` : 'Reply';

  return (
    <Stack ref={stripRef} alignItems="center" gap={1} data-testid="composer-strip">
      {offer ? (
        <StripGroup sx={offerGroupSx} data-testid="composer-start-folded-offer">
          <OfferQuestion>
            Start folded next time?
            <OfferButton variant="text" onClick={offer.onDecline}>
              Not now
            </OfferButton>
            <OfferButton variant="text" tone="accept" onClick={offer.onAccept}>
              Yes
            </OfferButton>
          </OfferQuestion>
        </StripGroup>
      ) : null}
      {/* The conversation page still renders under the v1 theme; the menu chrome
          (radius, border, surface) lives in the v2 MuiMenu override. The icon-only tooltips
          take the v2 MuiTooltip override the same way; v2 restyles no buttons. */}
      <V2ThemeProvider>
        <StripGroup ref={buttonsRef} data-labels={labels}>
          <Tooltip
            placement="top"
            title={
              isIconOnly ? (
                <ShortcutText
                  label={replyLabel}
                  shortcut={appShortcuts.openComposerReply.command}
                  size="small"
                />
              ) : (
                ''
              )
            }
          >
            <StripButton
              iconOnly={isIconOnly}
              onClick={onReply}
              startIcon={<DynamicIcon name="reply" library="lucide" size={STRIP_ICON_SIZE} />}
              aria-label={replyLabel}
              data-testid="composer-strip-reply"
            >
              <span>Reply</span>
              {requesterFirstName ? (
                <StripRecipient>to {requesterFirstName}</StripRecipient>
              ) : null}
              {showKeyHints ? (
                <ShortcutText
                  size="xs"
                  shortcut={appShortcuts.openComposerReply.command}
                  data-testid="composer-strip-key-hint"
                />
              ) : null}
            </StripButton>
          </Tooltip>
          <Tooltip
            placement="top"
            title={
              isIconOnly ? (
                <ShortcutText
                  label="Add note"
                  shortcut={appShortcuts.openComposerNote.command}
                  size="small"
                />
              ) : (
                ''
              )
            }
          >
            <StripButton
              iconOnly={isIconOnly}
              iconTone="note"
              onClick={onAddNote}
              startIcon={
                <DynamicIcon name="sticky-note" library="lucide" size={STRIP_ICON_SIZE} />
              }
              aria-label="Add note"
              data-testid="composer-strip-note"
            >
              <span>Add note</span>
              {showKeyHints ? (
                <ShortcutText
                  size="xs"
                  shortcut={appShortcuts.openComposerNote.command}
                  data-testid="composer-strip-key-hint"
                />
              ) : null}
            </StripButton>
          </Tooltip>
          <Tooltip placement="top" title={isMoreIconOnly && !moreAnchor ? 'More' : ''}>
            <StripButton
              iconOnly={isMoreIconOnly}
              onClick={openMore}
              startIcon={
                <DynamicIcon name="ellipsis" library="lucide" size={STRIP_ICON_SIZE} />
              }
              aria-label="More"
              aria-haspopup="menu"
              aria-expanded={Boolean(moreAnchor)}
              data-testid="composer-strip-more"
            >
              <span>More</span>
            </StripButton>
          </Tooltip>
          <EllipsesMenu
            hideTrigger
            open={Boolean(moreAnchor)}
            anchorEl={moreAnchor}
            onClose={closeMore}
            options={moreOptions}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          />
        </StripGroup>
      </V2ThemeProvider>
    </Stack>
  );
};
